import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";

const PurchasePaymentPrint = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data, isLoading, error } = useFetchData(
        `purchase-payment/${id}/show`,
    );

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() =>
                        navigate(`/purchase-payment/${id}/detail`)
                    }
                >
                    Kembali ke Detail
                </Button>
            </div>
        );
    }

    const payment = data;
    const items = payment?.items || [];
    const totalDiscount = items.reduce(
        (sum, item) => sum + (item.discount || 0),
        0,
    );

    return (
        <div className="mx-auto max-w-[210mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() =>
                        navigate(`/purchase-payment/${id}/detail`)
                    }
                >
                    Kembali ke Detail
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title = `Pembayaran_${payment?.payment_number || "Pembelian"}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="BUKTI PEMBAYARAN PEMBELIAN" />

                {/* Payment Info */}
                <div className="mb-4 grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
                    <div className="flex">
                        <span className="w-36 font-medium">No. Pembayaran</span>
                        <span>: {payment?.payment_number}</span>
                    </div>
                    <div className="flex">
                        <span className="w-36 font-medium">Tanggal</span>
                        <span>
                            :{" "}
                            {formatDate(payment?.payment_date, "dd MMM yyyy")}
                        </span>
                    </div>
                    <div className="flex">
                        <span className="w-36 font-medium">Supplier</span>
                        <span>: {payment?.supplier?.name || "-"}</span>
                    </div>
                    <div className="flex">
                        <span className="w-36 font-medium">Kas / Bank</span>
                        <span>: {payment?.master_cash?.name || "-"}</span>
                    </div>
                    {payment?.description && (
                        <div className="col-span-2 flex">
                            <span className="w-36 font-medium">Keterangan</span>
                            <span>: {payment.description}</span>
                        </div>
                    )}
                </div>

                {/* Items Table */}
                <table className="mb-4 w-full border-collapse border border-black text-xs">
                    <thead>
                        <tr className="bg-default-100">
                            <th className="border border-black p-1.5 text-center">
                                No.
                            </th>
                            <th className="border border-black p-1.5 text-left">
                                No. PI
                            </th>
                            <th className="border border-black p-1.5 text-left">
                                Tanggal
                            </th>
                            <th className="border border-black p-1.5 text-left">
                                Status Bayar
                            </th>
                            <th className="border border-black p-1.5 text-left">
                                Keterangan
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Total PI
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Diskon
                            </th>
                            <th className="border border-black p-1.5 text-right">
                                Jumlah Bayar
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={item.id}>
                                <td className="border border-black p-1.5 text-center">
                                    {index + 1}.
                                </td>
                                <td className="border border-black p-1.5 font-medium">
                                    {item.order_number || "-"}
                                </td>
                                <td className="border border-black p-1.5">
                                    {item.order_date
                                        ? formatDate(
                                              item.order_date,
                                              "dd MMM yyyy",
                                          )
                                        : "-"}
                                </td>
                                <td className="border border-black p-1.5">
                                    {item.payment_status || "-"}
                                </td>
                                <td className="border border-black p-1.5">
                                    {item.description || "-"}
                                </td>
                                <td className="border border-black p-1.5 text-right">
                                    {formatCurrency(item.po_total_amount)}
                                </td>
                                <td className="border border-black p-1.5 text-right">
                                    {item.discount > 0
                                        ? formatCurrency(item.discount)
                                        : "-"}
                                </td>
                                <td className="border border-black p-1.5 text-right font-medium">
                                    {formatCurrency(item.amount)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-default-100 font-bold">
                            <td
                                colSpan={6}
                                className="border border-black p-1.5 text-center"
                            >
                                TOTAL
                            </td>
                            <td className="border border-black p-1.5 text-right">
                                {totalDiscount > 0
                                    ? formatCurrency(totalDiscount)
                                    : "-"}
                            </td>
                            <td className="border border-black p-1.5 text-right">
                                {formatCurrency(payment?.total_amount)}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                {/* Signature area */}
                <div className="mt-12 grid grid-cols-2 gap-8 text-xs">
                    <div className="text-center">
                        <p className="font-medium">Dibuat oleh</p>
                        <div className="mt-16">
                            (..................................)
                        </div>
                    </div>
                    <div className="text-center">
                        <p className="font-medium">Disetujui oleh</p>
                        <div className="mt-16">
                            (..................................)
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-8 space-y-3 text-xs text-default-500">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                    <div className="flex flex-col gap-1">
                        <p className="font-medium">Catatan:</p>
                        <ol className="list-disc space-y-0.5 pl-4">
                            <li>
                                Dokumen ini merupakan bukti pembayaran yang sah
                                atas purchase order yang tercantum di atas.
                            </li>
                            <li>
                                Pembayaran yang telah dicatat tidak dapat
                                dibatalkan tanpa persetujuan pihak yang
                                berwenang.
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PurchasePaymentPrint;
